import { type StaticImageData } from "next/image";
import { integrationsCount } from "../../constants/integrations";
import PrivacyChoicesIcon from "../../../public/assets/privacy/privacy-choices.svg";

export type Group = {
  title: string;
  sub: Subgroup[];
};

export type Subgroup = {
  title: string;
  nav: Item[];
};

export type Item = {
  title: string;
  url?: string;
  icon?: Icon;
  onClick?: () => void;
};

export type Icon = {
  src: StaticImageData;
  alt: string;
};

export const config: Group[] = [
  {
    title: "Platform",
    sub: [
      {
        title: "Composable CDP",
        nav: [
          {
            title: "Composable CDP",
            url: "/platform/composable-cdp",
          },
          {
            title: "Reverse ETL",
            url: "/platform/reverse-etl",
          },
          {
            title: "Customer Studio",
            url: "/platform/customer-studio",
          },
          {
            title: "Identity Resolution",
            url: "/platform/identity-resolution",
          },
          {
            title: "Hightouch Events",
            url: "/platform/events",
          },
          {
            title: "Real-time Personalization",
            url: "/platform/real-time-personalization",
          },
          {
            title: "Match Booster",
            url: "/platform/match-booster",
          },
          {
            title: "Campaign Intelligence",
            url: "/platform/campaign-intelligence",
          },
        ],
      },
      {
        title: "AI Decisioning",
        nav: [
          {
            title: "AI Decisioning",
            url: "/platform/ai-decisioning",
          },
          {
            title: "AI agents",
            url: "/platform/ai-decisioning#ai-agents",
          },
          {
            title: "Reinforcement learning",
            url: "/blog/rl-agents-for-marketing",
          },
          {
            title: "Insights",
            url: "/platform/ai-decisioning#insights",
          },
        ],
      },
      {
        title: "Features",
        nav: [
          {
            title: "Enterprise-level security",
            url: "/platform/security",
          },
          {
            title: "Observability",
            url: "/platform/observability",
          },
          {
            title: "Extensions",
            url: "/platform/extensions",
          },
          {
            title: "Governance & access control",
            url: "/docs/workspace-management/overview",
          },
        ],
      },
    ],
  },
  {
    title: "Solutions",
    sub: [
      {
        title: "By team",
        nav: [
          {
            title: "Marketing",
            url: "/solutions/marketing",
          },
          {
            title: "Advertising",
            url: "/solutions/advertising",
          },
          {
            title: "Data",
            url: "/solutions/data",
          },
          {
            title: "Operations",
            url: "/solutions/operations",
          },
        ],
      },
      {
        title: "By industry",
        nav: [
          {
            title: "Retail & eCommerce",
            url: "/solutions/retail",
          },
          {
            title: "Media & Entertainment",
            url: "/solutions/media",
          },
          {
            title: "Financial Services",
            url: "/solutions/financial-services",
          },
          {
            title: "Healthcare",
            url: "/solutions/healthcare",
          },

          {
            title: "B2B SaaS",
            url: "/solutions/saas",
          },
        ],
      },
      {
        title: "For marketing",
        nav: [
          {
            title: "Audience segmentation",
            url: "/platform/customer-studio",
          },
          {
            title: "Journey orchestration",
            url: "/platform/customer-studio#journeys",
          },
          {
            title: "Real-time personalization",
            url: "/platform/real-time-personalization",
          },
          {
            title: "Profile enrichment",
            url: "/platform/customer-studio#traits",
          },
        ],
      },
      {
        title: "For advertising",
        nav: [
          {
            title: "Audience targeting",
            url: "/playbooks/improve-conversions-with-personalized-funnel-based-ad-retargeting",
          },
          {
            title: "Audience suppression",
            url: "/playbooks/Suppress-Existing-Customers-from-Facebook-Campaigns",
          },
          {
            title: "Conversion tracking",
            url: "/playbooks/send-better-signals-with-facebook-conversion-api",
          },
          {
            title: "Match Rate boosting",
            url: "/platform/match-booster",
          },
        ],
      },
    ],
  },
  {
    title: "Integrations",
    sub: [
      {
        title: `All ${integrationsCount}+ integrations`,
        nav: [
          {
            title: "Integrations catalog",
            url: "/integrations",
          },
        ],
      },
      {
        title: "Popular sources",
        nav: [
          {
            title: "Snowflake",
            url: "/solutions/snowflake-cdp",
          },
          {
            title: "Databricks",
            url: "/solutions/databricks-cdp",
          },
          {
            title: "Google BigQuery",
            url: "/solutions/google-bigquery-cdp",
          },
          {
            title: "AWS Redshift",
            url: "/solutions/aws-redshift-cdp",
          },
          {
            title: "Azure Synapse",
            url: "/solutions/azure-synapse-cdp",
          },
          {
            title: "See all sources",
            url: "/integrations?type=source",
          },
        ],
      },
      {
        title: "Popular destinations",
        nav: [
          {
            title: "Salesforce",
            url: "/integrations/destinations/salesforce",
          },
          {
            title: "HubSpot",
            url: "/integrations/destinations/hubspot",
          },
          {
            title: "Google Ads",
            url: "/integrations/destinations/google",
          },
          {
            title: "Facebook Ads",
            url: "/integrations/destinations/facebook",
          },
          {
            title: "Iterable",
            url: "/integrations/destinations/iterable",
          },
          {
            title: "Braze",
            url: "/integrations/destinations/braze",
          },
          {
            title: "See all destinations",
            url: "/integrations?type=destination",
          },
        ],
      },
      {
        title: "Popular extensions",
        nav: [
          {
            title: "dbt",
            url: "/solutions/dbt",
          },
          {
            title: "Sigma",
            url: "/solutions/sigma",
          },
          {
            title: "Looker",
            url: "/docs/extensions/looker-models",
          },
          {
            title: "Fivetran",
            url: "/solutions/fivetran",
          },
        ],
      },
    ],
  },
  {
    title: "Resources",
    sub: [
      {
        title: "Explore",
        nav: [
          {
            title: "Customer stories",
            url: "/customers",
          },
          {
            title: "Learn",
            url: "/learn",
          },
          {
            title: "Blog",
            url: "/blog/category/all/page/1",
          },

          {
            title: "Whitepapers & guides",
            url: "/resources",
          },
          {
            title: "Playbooks",
            url: "/playbooks",
          },
          {
            title: "Data Activators",
            url: "https://www.youtube.com/channel/UCTiIAJWiX6KXjGyd6umKl8w",
          },
          {
            title: "CDP Comparisons",
            url: "/compare-cdps",
          },
          {
            title: "CDP Industries Hub",
            url: "/cdp-industries",
          },
          {
            title: "Composable CDP Hub",
            url: "/solutions/composable-cdp",
          },
          {
            title: "SQL Dictionary",
            url: "/sql-dictionary",
          },
          {
            title: "Changelog",
            url: "https://changelog.hightouch.io/",
          },
        ],
      },
      {
        title: "Documentation",
        nav: [
          {
            title: "Core concepts",
            url: "/docs/getting-started/concepts",
          },
          {
            title: "Syncs",
            url: "/docs/syncs/overview",
          },
          {
            title: "Models",
            url: "/docs/models/creating-models",
          },
          {
            title: "Read our docs",
            url: "/docs",
          },
        ],
      },
      {
        title: "Get started",
        nav: [
          {
            title: "Talk to us",
            url: "/demo",
          },
          {
            title: "Events",
            url: "/events",
          },
          {
            title: "Solutions partners",
            url: "/solutions-partners",
          },
        ],
      },
      {
        title: "From the blog",
        nav: [
          {
            title: "What is AI Decisioning?",
            url: "/blog/ai-decisioning",
          },
          {
            title: "What is Reverse ETL?",
            url: "/blog/reverse-etl",
          },
          {
            title: "What is Data Activation?",
            url: "/blog/what-is-data-activation",
          },
          {
            title: "Friends Don’t Let Friends Buy a CDP",
            url: "/blog/friends-dont-let-friends-buy-a-cdp",
          },
        ],
      },
    ],
  },
  {
    title: "Company",
    sub: [
      {
        title: "Information",
        nav: [
          {
            title: "Careers",
            url: "/careers",
          },
          {
            title: "About",
            url: "/about",
          },
          {
            title: "Pricing",
            url: "/pricing",
          },
          {
            title: "Press",
            url: "/press",
          },
        ],
      },
      {
        title: "Legal & Privacy",
        nav: [
          {
            title: "Privacy policy",
            url: "/privacy-policy",
          },
          {
            title: "Cookie policy",
            url: "/cookie-policy",
          },
          {
            title: "Platform privacy notice",
            url: "/platform-privacy",
          },
          {
            title: "Terms of service",
            url: "/terms-of-service",
          },
          {
            title: "Cookie preferences",
            onClick() {
              // eslint-disable-next-line new-cap
              window?.OneTrust.ToggleInfoDisplay();
            },
          },
          {
            title: "Your privacy choices",
            url: "https://preferences.hightouch.com",
            icon: { src: PrivacyChoicesIcon, alt: "California Consumer Privacy Act (CCPA) Opt-Out Icon" },
          },
        ],
      },
    ],
  },
];
